import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  faArrowUpRightFromSquare,
  faEdit,
  faInfoCircle,
} from "@fortawesome/pro-regular-svg-icons";
import { isAxiosError } from "axios";
import {
  Controller,
  UseFormReturn,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { twJoin } from "tailwind-merge";

import {
  FlowToolConfigDrawerData,
  SideDrawer,
  useSideDrawerCoordinator,
} from "src/agents/base/SideDrawer";
import { AgentVersionConfigurationInput } from "src/agents/types";
import { PropertyValue, Schema, SchemaTypes } from "src/api/flowTypes";
import { useFlowVersion } from "src/api/flowVersionQueries";
import { useFlowBySlug } from "src/api/queries";
import { TOOL_ICON_CONFIG } from "src/base-components/agents/ToolCard";
import { FixedSimpleDropdown } from "src/base-components/FixedSimpleDropdown";
import { Input } from "src/base-components/Input";
import { Select } from "src/base-components/Select";
import { TabData, Tabs } from "src/base-components/Tabs";
import { Textarea } from "src/base-components/Textarea";
import { SCHEMA_TYPE_ICONS } from "src/base-components/TypeIcons";
import {
  AgentVersionConfigurationInputToolsInner,
  FlowToolExecutionMode,
  MetadataOverlay,
} from "src/clients/agent-management";
import { FlowVersionStatus } from "src/clients/flow-api";
import { Accordion } from "src/design-system/Accordion";
import { Button } from "src/design-system/Button";
import { Callout } from "src/design-system/Callout";
import { Icon, IconButton } from "src/design-system/Icon";
import { Skeleton } from "src/design-system/Skeleton";
import { Tooltip } from "src/design-system/Tooltip";
import { VersionStatusPill } from "src/flow/Pills";
import { UserNameInfo } from "src/flowContainer/versionEditedInfo/UsernameInfo";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { SchemaOptions } from "src/router/SearchParams";
import { getBaseUrl, getUrlToAuthoringPage } from "src/router/urls";
import { PropertyConverter } from "src/schema/schemaMappingUtils";
import { formatDate } from "src/utils/datetime";

// Accordion section IDs
const SECTION_FLOW = "flow";
const SECTION_BASIC = "basic";
const SECTION_SCHEMA = "schema";
const SECTION_ADVANCED = "advanced";

// Helper to detect if a resource was deleted (403 after deletion or 404 not found)
const isResourceDeleted = (error: Error | null) => {
  if (!error) return false;
  if (isAxiosError(error)) {
    const status = error.response?.status;
    return status === 403 || status === 404;
  }
  return false;
};

const EXECUTION_MODE_OPTIONS = [
  {
    key: FlowToolExecutionMode.SYNC,
    value: (
      <span className="flex items-center gap-1">
        Sync
        <Tooltip
          body="The Agent actively waits for this tool to finish before proceeding. Best for tools that return in seconds."
          placement="right"
        >
          <Icon icon={faInfoCircle} size="xs" />
        </Tooltip>
      </span>
    ),
    displayValue: "Sync",
  },
  {
    key: FlowToolExecutionMode.ASYNC,
    value: (
      <span className="flex items-center gap-1">
        Async
        <Tooltip
          body="The Agent suspends and is notified when the tool finishes, then resumes from where it left off. Best for tools that take a long time to respond."
          placement="right"
        >
          <Icon icon={faInfoCircle} size="xs" />
        </Tooltip>
      </span>
    ),
    displayValue: "Async",
  },
];

type FlowToolFormValues = {
  name: string;
  description: string;
  inputMetadataOverlay: MetadataOverlay;
  outputMetadataOverlay: MetadataOverlay;
  toolFlowExecutionMode: FlowToolExecutionMode;
};

/**
 * Hook to access a tool by its id using watch/setValue with full array replacement.
 */
const useToolById = (toolId: string) => {
  const { setValue, getValues, control } =
    useFormContext<AgentVersionConfigurationInput>();
  const tools = useWatch({
    name: "tools",
    control,
  });
  const tool = tools?.find((t) => t.id === toolId);
  const updateTool = useCallback(
    (updatedTool: AgentVersionConfigurationInputToolsInner) => {
      // Get fresh tools value and replace entire array to ensure re-render
      const currentTools = getValues("tools") ?? [];
      setValue(
        "tools",
        currentTools.map((t) => (t.id === toolId ? updatedTool : t)),
      );
    },
    [setValue, getValues, toolId],
  );

  return { tool, updateTool };
};

/** Helper component to get tool name from form context for header */
const FlowToolHeaderTitle: React.FC<{ toolId: string }> = ({ toolId }) => {
  const { tool } = useToolById(toolId);
  return <>{tool?.name || "Tool"}</>;
};

export const FlowToolConfigDrawer: React.FC<{ immutable: boolean }> = ({
  immutable,
}) => {
  const { openDrawer } = useSideDrawerCoordinator();
  const data =
    openDrawer?.type === "flowToolConfig" ? openDrawer.data : undefined;

  return (
    <SideDrawer
      dataLoc="flow-tool-config-drawer"
      drawerId="flowToolConfig"
      header={{
        title: data ? <FlowToolHeaderTitle toolId={data.toolId} /> : "Tool",
        description: "Decision Flow Tool",
        closeDataLoc: "close-flow-tool-config-drawer",
        reverseTitleAndDescription: true,
        icon: TOOL_ICON_CONFIG.flow && (
          <div
            className={twJoin(
              "flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg",
              TOOL_ICON_CONFIG.flow.bgColor,
            )}
          >
            <Icon
              variant={TOOL_ICON_CONFIG.flow.iconVariant}
              icon={TOOL_ICON_CONFIG.flow.icon}
              size="sm"
            />
          </div>
        ),
      }}
    >
      {data && <FlowToolConfigDrawerContent {...data} immutable={immutable} />}
    </SideDrawer>
  );
};

const FlowToolConfigDrawerContent: React.FC<
  FlowToolConfigDrawerData & { immutable: boolean }
> = ({ toolId, onRequestChangeFlow, immutable }) => {
  // Get tool from form context by id
  const { tool, updateTool } = useToolById(toolId);

  // Fetch flow data for display (hook is auto-disabled when slug is empty)
  const { workspace } = useWorkspaceContext();
  const flowQuery = useFlowBySlug(workspace.id, tool?.flow_slug, {
    refetchOnMount: true,
    shallow: true,
  });
  const flow = flowQuery.data;
  const flowError = flowQuery.error;
  const versionQuery = useFlowVersion(tool?.flow_version_id, true);
  const version = versionQuery.data;
  const versionError = versionQuery.error;

  // Local form for all editable fields
  const localForm = useForm<FlowToolFormValues>({
    defaultValues: {
      name: tool?.name ?? "",
      description: tool?.description ?? "",
      inputMetadataOverlay: tool?.input_schema_metadata_overlay ?? {
        properties: {},
      },
      outputMetadataOverlay: tool?.output_schema_metadata_overlay ?? {
        properties: {},
      },
      toolFlowExecutionMode:
        tool?.tool_flow_execution_mode ?? FlowToolExecutionMode.ASYNC,
    },
    mode: "onChange",
  });

  // Accordion state
  const [expandedSections, setExpandedSections] = useState<string[]>([
    SECTION_FLOW,
    SECTION_BASIC,
  ]);

  // Derive flow info status for discriminated union
  const flowInfoStatus = useMemo(() => {
    if (flowError && isResourceDeleted(flowError))
      return "flow-deleted" as const;
    if (versionError && isResourceDeleted(versionError))
      return "version-deleted" as const;
    if (!flow || !version) return "loading" as const;
    return "loaded" as const;
  }, [flow, version, flowError, versionError]);

  // Sync overlay helper - rebuilds overlay from schema, preserving existing descriptions
  // and prefilling empty ones from schema descriptions
  const syncOverlay = useCallback(
    (
      schema: Schema | undefined,
      existingOverlay: MetadataOverlay,
    ): MetadataOverlay => {
      const schemaProps = schema?.properties ?? {};
      const overlayProps = existingOverlay.properties ?? {};

      // Rebuild overlay with current schema properties
      const newOverlay: MetadataOverlay = { properties: {} };
      for (const propName of Object.keys(schemaProps)) {
        const schemaProp = schemaProps[propName];
        newOverlay.properties[propName] = {
          description:
            overlayProps[propName]?.description ??
            schemaProp?.description ??
            "",
        };
      }
      return newOverlay;
    },
    [],
  );

  // Reset form when tool changes, syncing overlays with the fetched version schema
  useEffect(() => {
    if (!tool) return;

    // Build overlays - if version is loaded, sync with its schema to get descriptions
    const inputOverlay = version
      ? syncOverlay(
          version.input_schema,
          tool.input_schema_metadata_overlay ?? { properties: {} },
        )
      : (tool.input_schema_metadata_overlay ?? { properties: {} });

    const outputOverlay = version
      ? syncOverlay(
          version.output_schema,
          tool.output_schema_metadata_overlay ?? { properties: {} },
        )
      : (tool.output_schema_metadata_overlay ?? { properties: {} });

    localForm.reset({
      name: tool.name,
      description: tool.description ?? "",
      inputMetadataOverlay: inputOverlay,
      outputMetadataOverlay: outputOverlay,
      toolFlowExecutionMode:
        tool.tool_flow_execution_mode ?? FlowToolExecutionMode.ASYNC,
    });

    // We dont want to run this if tool is updated
    // only if its a different tool or different version is selected
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool?.id, tool?.flow_version_id, version, localForm, syncOverlay]);

  // Subscribe to form changes and propagate to main form via updateTool
  useEffect(
    () =>
      localForm.subscribe({
        formState: { values: true },
        callback: async () => {
          if (!tool) return;
          const isLocalValid = await localForm.trigger();
          if (!isLocalValid) return;

          const values = localForm.getValues();
          updateTool({
            ...tool,
            name: values.name,
            description: values.description,
            input_schema_metadata_overlay: values.inputMetadataOverlay,
            output_schema_metadata_overlay: values.outputMetadataOverlay,
            tool_flow_execution_mode: values.toolFlowExecutionMode,
          });
        },
      }),
    [localForm, updateTool, tool, toolId],
  );

  return (
    <div className="flex h-full flex-col">
      <div className="decideScrollbar flex-1 overflow-y-auto p-5">
        <Accordion
          dataLoc="flow-tool-config-accordion"
          value={expandedSections}
          onChange={setExpandedSections}
        >
          <Accordion.Item
            dataLoc="tool-flow-section"
            label="Decision Flow"
            value={SECTION_FLOW}
          >
            <FlowInfoCard
              immutable={immutable}
              onEdit={onRequestChangeFlow}
              {...(flowInfoStatus === "loaded"
                ? { status: "loaded", flow: flow!, version: version! }
                : { status: flowInfoStatus })}
            />
          </Accordion.Item>

          <Accordion.Item
            dataLoc="tool-basic-details-section"
            rightSlot={
              <Tooltip
                body="Describe the tool's purpose and intent so the Agent can use it appropriately during execution."
                placement="right"
              >
                <Icon icon={faInfoCircle} size="xs" />
              </Tooltip>
            }
            label="Tool information"
            value={SECTION_BASIC}
          >
            <div className="mb-4 rounded-lg bg-gray-50 p-4">
              <div className="flex flex-col gap-4">
                <div>
                  <label className="mb-1.5 block font-inter-semibold-13px text-gray-800">
                    Name{" "}
                    <span className="font-inter-normal-12px text-indigo-500">
                      *
                    </span>
                  </label>
                  <Controller
                    control={localForm.control}
                    name="name"
                    render={({ field, fieldState }) => (
                      <Input
                        data-loc="tool-name-input"
                        disabled={immutable}
                        errored={!!fieldState.error}
                        placeholder="Enter tool name"
                        value={field.value}
                        fullWidth
                        onChange={field.onChange}
                      />
                    )}
                    rules={{ required: "Name is required" }}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block font-inter-semibold-13px text-gray-800">
                    Description{" "}
                  </label>
                  <Controller
                    control={localForm.control}
                    name="description"
                    render={({ field }) => (
                      <Textarea
                        data-loc="tool-description-input"
                        disabled={immutable}
                        placeholder="Enter tool description"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </Accordion.Item>

          <Accordion.Item
            dataLoc="tool-schema-section"
            label="Schema"
            value={SECTION_SCHEMA}
          >
            <SchemaSection
              flowInfoStatus={flowInfoStatus}
              form={localForm}
              immutable={immutable}
              version={version}
            />
          </Accordion.Item>

          <Accordion.Item
            dataLoc="tool-advanced-settings-section"
            label="Advanced settings"
            value={SECTION_ADVANCED}
          >
            <div className="mb-4 rounded-lg bg-gray-50 p-4">
              <div>
                <label className="mb-1.5 block font-inter-semibold-13px text-gray-800">
                  Execution mode
                </label>
                <Controller
                  control={localForm.control}
                  name="toolFlowExecutionMode"
                  render={({ field }) => (
                    <Select
                      dataLoc="tool-execution-mode-select"
                      disabled={immutable}
                      options={EXECUTION_MODE_OPTIONS}
                      placeholder="Select execution mode"
                      value={field.value}
                      fullWidth
                      onChange={(value) =>
                        field.onChange(value as FlowToolExecutionMode)
                      }
                    />
                  )}
                />
              </div>
            </div>
          </Accordion.Item>
        </Accordion>
      </div>
    </div>
  );
};

// ============================================================================
// Flow Info Card Component
// ============================================================================

type FlowInfoCardBaseProps = {
  immutable: boolean;
  onEdit: () => void;
};

type FlowInfoCardProps = FlowInfoCardBaseProps &
  (
    | { status: "loading" }
    | { status: "flow-deleted" }
    | { status: "version-deleted" }
    | {
        status: "loaded";
        flow: { id: string; name: string; meta: { description?: string } };
        version: {
          id: string;
          name: string;
          status: FlowVersionStatus;
          updated_at: string;
          meta?: {
            release_note?: string;
            published_at?: string;
            published_by_id?: string;
            updated_by_id?: string;
            created_by_id?: string;
          };
        };
      }
  );

const FlowInfoCard: React.FC<FlowInfoCardProps> = (props) => {
  const { workspace, orgId } = useWorkspaceContext();
  const { immutable, onEdit, status } = props;

  if (status === "flow-deleted") {
    return (
      <div className="mb-4 rounded-lg bg-gray-50 p-4">
        <div className="space-y-3">
          <div className="font-inter-semibold-13px text-gray-800">
            Select Decision flow and version{" "}
            <span className="font-inter-normal-12px text-indigo-500">*</span>
          </div>
          <Callout variant="error">
            The selected Decision Flow no longer exists. Please select a
            different flow.
          </Callout>
          {!immutable && (
            <Button
              dataLoc="select-flow-button"
              iconLeft={faEdit}
              size="sm"
              variant="secondary"
              onClick={onEdit}
            >
              Select flow
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (status === "version-deleted") {
    return (
      <div className="mb-4 rounded-lg bg-gray-50 p-4">
        <div className="space-y-3">
          <div className="font-inter-semibold-13px text-gray-800">
            Select Decision flow and version{" "}
            <span className="font-inter-normal-12px text-indigo-500">*</span>
          </div>
          <Callout variant="error">
            The selected flow version no longer exists. Please select a
            different version.
          </Callout>
          {!immutable && (
            <Button
              dataLoc="select-version-button"
              iconLeft={faEdit}
              size="sm"
              variant="secondary"
              onClick={onEdit}
            >
              Select version
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (status === "loading") {
    return (
      <div className="mb-4 rounded-lg bg-gray-50 p-4">
        <div className="space-y-3">
          <Skeleton height="h-4" width="w-48" />
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="space-y-2">
              <Skeleton height="h-5" width="w-40" />
              <Skeleton height="h-4" width="w-full" />
              <Skeleton height="h-4" width="w-56" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { flow, version } = props;
  const isPublished = version.status === FlowVersionStatus.PUBLISHED;
  const date = isPublished ? version.meta?.published_at : version.updated_at;
  const userId = isPublished
    ? version.meta?.published_by_id
    : version.meta?.updated_by_id || version.meta?.created_by_id;
  const formattedDate = date ? formatDate(date) : null;

  const handleOpenInNewTab = () => {
    const url =
      getBaseUrl() +
      getUrlToAuthoringPage(orgId, workspace.id, flow.id, version.id);
    window.open(url, "_blank");
  };

  return (
    <div className="mb-4 rounded-lg bg-gray-50 p-4">
      <div className="space-y-3">
        <div className="font-inter-semibold-13px text-gray-800">
          Select Decision flow and version{" "}
          <span className="font-inter-normal-12px text-indigo-500">*</span>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <span className="font-inter-semibold-13px text-gray-800">
                {flow.name}, {version.name}
              </span>
              <p className="mt-1 font-inter-normal-12px text-gray-500">
                {version.meta?.release_note ||
                  flow.meta.description ||
                  "No description"}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Tooltip body="Open in new tab" placement="top">
                <IconButton
                  dataLoc="open-flow-version-new-tab"
                  icon={faArrowUpRightFromSquare}
                  size="xs"
                  onClick={handleOpenInNewTab}
                />
              </Tooltip>
              <VersionStatusPill status={version.status} />
            </div>
          </div>
          {userId && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5 font-inter-normal-12px text-gray-600">
              {isPublished ? "Published by" : "Last edited by"}
              <span className="flex items-center font-inter-medium-12px text-gray-800">
                <UserNameInfo userId={userId} withAvatar />
              </span>
              {formattedDate && (
                <span className="whitespace-nowrap">
                  on{" "}
                  <span className="font-inter-medium-12px text-gray-800">
                    {formattedDate}
                  </span>
                </span>
              )}
            </div>
          )}
        </div>
        {!immutable && (
          <Button
            dataLoc="edit-flow-selection-button"
            iconLeft={faEdit}
            size="sm"
            variant="secondary"
            onClick={onEdit}
          >
            Edit
          </Button>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Schema Section Component
// ============================================================================

type SchemaSectionProps = {
  flowInfoStatus: "loading" | "flow-deleted" | "version-deleted" | "loaded";
  version?: {
    name: string;
    input_schema?: Schema;
    output_schema?: Schema;
  };
  form: UseFormReturn<FlowToolFormValues>;
  immutable: boolean;
};

const SchemaSection: React.FC<SchemaSectionProps> = ({
  flowInfoStatus,
  version,
  form,
  immutable,
}) => {
  if (flowInfoStatus === "flow-deleted") {
    return (
      <div className="mb-4 rounded-lg bg-gray-50 p-4">
        <p className="font-inter-normal-12px text-gray-500">
          Schema is unavailable because the selected Decision Flow no longer
          exists.
        </p>
      </div>
    );
  }

  if (flowInfoStatus === "version-deleted") {
    return (
      <div className="mb-4 rounded-lg bg-gray-50 p-4">
        <p className="font-inter-normal-12px text-gray-500">
          Schema is unavailable because the selected flow version no longer
          exists.
        </p>
      </div>
    );
  }

  if (!version) {
    return (
      <div className="mb-4 rounded-lg bg-gray-50 p-4">
        <div className="space-y-3">
          <Skeleton height="h-4" width="w-full" />
          <Skeleton height="h-8" width="w-24" />
          <Skeleton height="h-20" width="w-full" />
        </div>
      </div>
    );
  }

  return (
    <FlowToolSchemaViewer
      flowVersionName={version.name}
      form={form}
      immutable={immutable}
      inputSchema={version.input_schema}
      outputSchema={version.output_schema}
    />
  );
};

// ============================================================================
// Flow Tool Schema Viewer Component
// ============================================================================

type FlowToolSchemaViewerProps = {
  inputSchema?: Schema;
  outputSchema?: Schema;
  form: UseFormReturn<FlowToolFormValues>;
  flowVersionName?: string;
  immutable: boolean;
};

type SchemaTab = "input" | "output";

const SCHEMA_TYPE_OPTIONS = [
  { key: "string", value: "String", icon: SCHEMA_TYPE_ICONS.string },
  { key: "number", value: "Number", icon: SCHEMA_TYPE_ICONS.number },
  { key: "boolean", value: "Boolean", icon: SCHEMA_TYPE_ICONS.boolean },
  { key: "integer", value: "Integer", icon: SCHEMA_TYPE_ICONS.integer },
  { key: "array", value: "Array", icon: SCHEMA_TYPE_ICONS.array },
  { key: "object", value: "Object", icon: SCHEMA_TYPE_ICONS.object },
  { key: "datetime", value: "Datetime", icon: SCHEMA_TYPE_ICONS.datetime },
  { key: "date", value: "Date", icon: SCHEMA_TYPE_ICONS.date },
  {
    key: "datetime-str",
    value: "Datetime string",
    icon: SCHEMA_TYPE_ICONS["datetime-str"],
  },
  {
    key: "date-str",
    value: "Date string",
    icon: SCHEMA_TYPE_ICONS["date-str"],
  },
  { key: "enum", value: "Enum", icon: SCHEMA_TYPE_ICONS.enum },
  { key: "any", value: "Any", icon: SCHEMA_TYPE_ICONS.any },
] as const;

const getSchemaTypeOption = (type: SchemaTypes) => {
  return (
    SCHEMA_TYPE_OPTIONS.find((opt) => opt.key === type) ?? {
      key: type,
      value: type.charAt(0).toUpperCase() + type.slice(1),
      icon: SCHEMA_TYPE_ICONS.string,
    }
  );
};

/**
 * A flow schema encodes datetime and date fields as a string carrying a
 * `format` and a title marker, so the raw `type` alone renders every one of
 * them as "String". `typeSetterBE` is the same mapping the flow schema editor
 * uses to turn that triple back into a UI type.
 */
export const getPropertyTypeOption = (
  property: PropertyValue,
  schemaOption: SchemaOptions,
) =>
  getSchemaTypeOption(
    PropertyConverter.typeSetterBE(
      property.type,
      property.format,
      schemaOption,
      property.title,
      property.enum,
    ),
  );

const FlowToolSchemaViewer: React.FC<FlowToolSchemaViewerProps> = ({
  inputSchema,
  outputSchema,
  form,
  flowVersionName,
  immutable,
}) => {
  const [activeTab, setActiveTab] = useState<SchemaTab>("input");

  const tabs: TabData[] = useMemo(
    () => [
      {
        key: "input",
        label: "Input",
        content: (
          <SchemaPropertiesPanel
            form={form}
            immutable={immutable}
            overlayFieldName="inputMetadataOverlay"
            schema={inputSchema}
          />
        ),
        unmount: false,
      },
      {
        key: "output",
        label: "Output",
        content: (
          <SchemaPropertiesPanel
            form={form}
            immutable={immutable}
            overlayFieldName="outputMetadataOverlay"
            schema={outputSchema}
          />
        ),
        unmount: false,
      },
    ],
    [inputSchema, outputSchema, form, immutable],
  );

  return (
    <div>
      <p className="mb-2 font-inter-normal-12px text-gray-500">
        Configure the input and output fields for this tool
      </p>

      {flowVersionName && (
        <Callout variant="info">
          The input schema below is derived from "{flowVersionName}". You can
          add descriptions but cannot change the structure.
        </Callout>
      )}
      <div className="my-4 rounded-lg bg-gray-50 p-4">
        <Tabs
          selectedKey={activeTab}
          tabClassName="px-1.5"
          tabListClassName="gap-x-2 flex border-b border-gray-200"
          tabs={tabs}
          onChange={(key) => setActiveTab(key as SchemaTab)}
        />
      </div>
    </div>
  );
};

type SchemaPropertiesPanelProps = {
  schema?: Schema;
  overlayFieldName: "inputMetadataOverlay" | "outputMetadataOverlay";
  form: UseFormReturn<FlowToolFormValues>;
  immutable: boolean;
};

const SchemaPropertiesPanel: React.FC<SchemaPropertiesPanelProps> = ({
  schema,
  overlayFieldName,
  form,
  immutable,
}) => {
  const properties: Record<string, PropertyValue> = schema?.properties ?? {};
  const requiredFields = schema?.required ?? [];
  const schemaOption =
    overlayFieldName === "inputMetadataOverlay"
      ? SchemaOptions.Input
      : SchemaOptions.Output;

  if (Object.keys(properties).length === 0) {
    return (
      <p className="mt-3 font-inter-normal-12px text-gray-400">
        No properties defined
      </p>
    );
  }

  return (
    <div className="mt-9 ml-4">
      {/* Column headers */}
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <span className="font-inter-normal-12px text-gray-500">Name</span>
        </div>
        <div className="w-32">
          <span className="font-inter-normal-12px text-gray-500">Type</span>
        </div>
      </div>
      <div className="mt-2 space-y-4">
        {/* Schema properties */}
        {Object.entries(properties).map(([propName, propSchema]) => {
          const isRequired = requiredFields.includes(propName);
          const displayName = isRequired ? `${propName}*` : propName;
          const typeOption = getPropertyTypeOption(propSchema, schemaOption);
          const fieldName =
            `${overlayFieldName}.properties.${propName}.description` as const;

          return (
            <div key={propName} className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <Input value={displayName} disabled fullWidth />
                </div>
                <div className="w-32">
                  <FixedSimpleDropdown
                    elements={[typeOption]}
                    placeholder="Type"
                    selectedKey={typeOption.key}
                    disabled
                    onSelect={() => {}}
                  />
                </div>
              </div>
              <Controller
                control={form.control}
                name={fieldName}
                render={({ field }) => (
                  <Textarea
                    disabled={immutable}
                    placeholder="Add description..."
                    value={field.value ?? ""}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
